// Every string BudgetMeter can render, and the shape a caller may replace them
// with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, in bidCardTranslations.js.
//
// What is worth saying here is why the footer is four sentences rather than one
// with a clause glued onto its end. Where the reset day goes, and what joins it
// to the rest, is the translation's decision: BidCard's `proxyWide` keeps its
// separator inside the string for the same reason — French Canadian sets a
// space either side of a middot, and a language that would rather say when the
// week starts over at the front of the sentence can.
//
// There is no `formatMoney` member, as there is none on PlanningWorksheet's
// `i18n`: every figure on the card goes through the library's shared
// `formatPrice`, and one component taking its own money policy would be the
// first half of a library-wide decision taken in the wrong place. The object is
// closed, so passing one fails the validator rather than being ignored.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds that
// a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

export { interpolate } from './bidCardTranslations';

/**
 * The English defaults, frozen.
 *
 * `{amount}` is a formatted dollar figure and `{day}` the caller's `resetsOn`,
 * already in the reader's language. A translation may move them around its
 * sentence; it may not invent one, since the card only substitutes the names it
 * knows.
 */
export const BUDGET_METER_TRANSLATIONS = Object.freeze({
  // The eyebrow over the limit. Upper case is the card's CSS, not the string's.
  label: 'Weekly budget',

  // The footer while there is room: `{amount}` is what is left of the limit.
  remaining: '{amount} left this week',
  remainingWithReset: '{amount} left this week · resets {day}',

  // The footer past the limit: `{amount}` is how far over it the week has gone.
  // Two sentences rather than one with a sign in front of it, as
  // PlanningWorksheet's margin has it — "−$6,500 left" is not a phrase.
  over: '{amount} over this week',
  overWithReset: '{amount} over this week · resets {day}',
});

/**
 * The keys in `translations` that BudgetMeter would not read, as dotted paths.
 *
 * The walk itself is BidCard's — it already takes the defaults object as an
 * argument, so this surface passes its own rather than owning a second copy of
 * a comparison that has to behave identically everywhere.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, BUDGET_METER_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) return BUDGET_METER_TRANSLATIONS;

  return { ...BUDGET_METER_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidBudgetMeterI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  if (Object.keys(i18n).some((key) => key !== 'translations')) return false;
  if (i18n.translations === undefined) return true;
  return unreadTranslationKeys(i18n.translations).length === 0;
}
